import { Link } from '@tanstack/react-router';
import { type FC, type ReactNode } from 'react';

/** Max-width page container shared by every landing section. */
export const Container: FC<{ children: ReactNode; className?: string }> = ({
  children,
  className = ''
}) => <div className={`mx-auto w-full max-w-6xl px-5 sm:px-8 ${className}`}>{children}</div>;

/**
 * Signature element: an engineering-drawing title block. Every section opens
 * with one: a mono sheet reference over a hairline rule, then the display
 * heading. Encodes the company's cut-sheet/drawing culture.
 */
export const TitleBlock: FC<{
  sheet: string;
  title: string;
  lede?: string;
  dark?: boolean;
  id?: string;
}> = ({ sheet, title, lede, dark = false, id }) => (
  <header id={id} className="mb-10 sm:mb-14">
    <p
      className={`font-mono text-[11px] tracking-[0.2em] uppercase ${
        dark ? 'text-ink-400' : 'text-ink-500'
      }`}
    >
      {sheet}
    </p>
    <div className={`mt-2 mb-5 h-px w-full ${dark ? 'bg-ink-600' : 'bg-ink-200'}`} />
    <h2
      className={`font-display text-3xl leading-tight font-bold sm:text-4xl ${
        dark ? 'text-white' : 'text-ink-900'
      }`}
    >
      {title}
    </h2>
    {lede ? (
      <p className={`mt-4 max-w-2xl text-base sm:text-lg ${dark ? 'text-ink-300' : 'text-ink-600'}`}>
        {lede}
      </p>
    ) : null}
  </header>
);

/**
 * Signature motif: a CAD dimension line, used to "measure" a claim.
 * |<---- label ---->|
 */
export const DimensionLine: FC<{ label: string; className?: string }> = ({
  label,
  className = ''
}) => (
  <div aria-hidden="true" className={`flex items-center gap-3 ${className}`}>
    <span className="h-4 w-px bg-brand-500" />
    <span className="relative h-px flex-1 bg-brand-500/70">
      <span className="absolute top-1/2 left-0 h-1.5 w-1.5 -translate-y-1/2 rotate-45 border-b border-l border-brand-500" />
      <span className="absolute top-1/2 right-0 h-1.5 w-1.5 -translate-y-1/2 rotate-45 border-t border-r border-brand-500" />
    </span>
    <span className="font-mono text-xs whitespace-nowrap text-brand-400 sm:text-sm">{label}</span>
    <span className="relative h-px flex-1 bg-brand-500/70">
      <span className="absolute top-1/2 left-0 h-1.5 w-1.5 -translate-y-1/2 rotate-45 border-b border-l border-brand-500" />
      <span className="absolute top-1/2 right-0 h-1.5 w-1.5 -translate-y-1/2 rotate-45 border-t border-r border-brand-500" />
    </span>
    <span className="h-4 w-px bg-brand-500" />
  </div>
);

const buttonBase =
  'inline-flex items-center justify-center gap-2 rounded-md px-6 py-3.5 text-sm font-semibold transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500';

export const PrimaryLink: FC<{
  to: string;
  children: ReactNode;
  className?: string;
}> = ({ to, children, className = '' }) => (
  <Link
    to={to}
    className={`${buttonBase} bg-brand-600 text-white hover:bg-brand-700 ${className}`}
  >
    {children}
  </Link>
);

export const GhostLink: FC<{
  to: string;
  children: ReactNode;
  dark?: boolean;
  className?: string;
}> = ({ to, children, dark = false, className = '' }) => (
  <Link
    to={to}
    className={`${buttonBase} border ${
      dark
        ? 'border-white/40 text-white hover:border-white hover:bg-white/10'
        : 'border-ink-300 text-ink-800 hover:border-ink-800'
    } ${className}`}
  >
    {children}
  </Link>
);

export const PhoneLink: FC<{ className?: string; children?: ReactNode }> = ({
  className = '',
  children
}) => (
  <a href="tel:+6421412384" className={className} data-conversion="phone-click">
    {children ?? '021 412 384'}
  </a>
);

/** Full-width dark call-to-action band closing most pages. */
export const CtaBand: FC<{
  title?: string;
  lede?: string;
}> = ({
  title = 'Ready to get concrete on the ground?',
  lede = 'Send us your plans or measurements and we’ll come back with a fixed quote, usually within two working days.'
}) => (
  <section className="bg-ink-900 py-16 sm:py-20">
    <Container className="flex flex-col items-start gap-8 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">{title}</h2>
        <p className="mt-3 max-w-xl text-ink-300">{lede}</p>
      </div>
      <div className="flex flex-shrink-0 flex-col gap-3 sm:flex-row">
        <PrimaryLink to="/quote">Get a free quote</PrimaryLink>
        <PhoneLink
          className={`${buttonBase} border border-white/40 text-white hover:border-white hover:bg-white/10`}
        >
          Call 021 412 384
        </PhoneLink>
      </div>
    </Container>
  </section>
);
